import React, { useEffect, useState } from 'react'
import Header from '../Header/Header'
import OrderCard, { AccountOrder } from '../OrderCard/OrderCard'
import WindowedPagination from '../WindowedPagination/WindowedPagination'

export interface AccountOrderHistoryProps {
  /**
   * List of orders to be displayed.
   */
  orders?: AccountOrder[]
  /**
   * Callback function when the view details button is pressed.
   */
  viewDetails: (trackingId?: string) => void
  /**
   * Callback function when the book again button is pressed.
   */
  bookAgain: (order: AccountOrder) => void
  /**
   * Whether more orders exist beyond the ones loaded so far.
   */
  hasMore?: boolean
  /**
   * Whether the next batch of orders is currently being fetched.
   */
  isLoadingMore?: boolean
  /**
   * Callback to fetch the next batch of orders. Called when the user pages
   * past the last loaded order.
   */
  onLoadMore?: () => void
}

export default function AccountOrderHistory({
  orders,
  viewDetails,
  bookAgain,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: AccountOrderHistoryProps) {
  const dateFormatOptions = {
    month: 'long',
    year: 'numeric',
  } as Intl.DateTimeFormatOptions

  const [currentPage, setCurrentPage] = useState(1)
  const [isAdvancePending, setIsAdvancePending] = useState(false)

  const itemsPerPage = 5
  const totalPages = Math.ceil((orders?.length || 0) / itemsPerPage)

  // Advance onto the newly loaded page once a load-more fetch lands.
  useEffect(() => {
    if (isAdvancePending && currentPage < totalPages) {
      setCurrentPage((page) => page + 1)
      setIsAdvancePending(false)
    }
  }, [currentPage, isAdvancePending, totalPages])

  if (!orders || orders.length === 0) {
    return (
      <div className="border border-neutral-400 rounded-8 p-8 text-neutral-500 text-sm">
        <Header as="h3" className="type-body-3">
          (No orders.)
        </Header>
        <p className="mt-4">
          Once you complete an order, you can view them here.
        </p>
      </div>
    )
  }

  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedOrders = orders.slice(startIndex, endIndex)

  return (
    <div className="lg:border lg:border-neutral-400 lg:rounded-8 lg:p-8">
      {paginatedOrders.map((order, index) => {
        const [outboundLeg, returnLeg] =
          order?.legs?.filter(({ state }) => state === 'active') || []

        const currentOrderMonth = new Date(order.createdAt).toLocaleDateString(
          'en-US',
          dateFormatOptions,
        )

        const showHeader =
          index === 0 ||
          currentOrderMonth !==
            new Date(paginatedOrders[index - 1].createdAt).toLocaleDateString(
              'en-US',
              dateFormatOptions,
            )

        return (
          <React.Fragment key={index}>
            {showHeader && (
              <Header
                className="type-body-1 lg:type-body-3 [&:not(:first-child)]:mt-6 lg:[&:not(:first-child)]:mt-8"
                as="h3"
              >
                {currentOrderMonth}
              </Header>
            )}
            <OrderCard
              order={order}
              origin={outboundLeg?.shipRoute?.origin}
              destination={outboundLeg?.shipRoute?.destination}
              leg={outboundLeg}
              viewDetails={{
                onClick: () =>
                  viewDetails(outboundLeg?.shipments?.[0]?.trackingId),
              }}
              bookAgain={{
                onClick: () => bookAgain(order),
              }}
            />
            {returnLeg && (
              <OrderCard
                order={order}
                origin={returnLeg?.shipRoute?.origin}
                destination={returnLeg?.shipRoute?.destination}
                leg={returnLeg}
                viewDetails={{
                  onClick: () =>
                    viewDetails(returnLeg?.shipments?.[0]?.trackingId),
                }}
                bookAgain={{
                  onClick: () => bookAgain(order),
                }}
              />
            )}
          </React.Fragment>
        )
      })}

      <div className="mt-8">
        <WindowedPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          hasMore={hasMore}
          isLoading={isLoadingMore}
          onLoadMore={() => {
            // More orders exist remotely; fetch the next batch and advance onto
            // it once it lands (see the isAdvancePending effect above).
            setIsAdvancePending(true)
            onLoadMore?.()
          }}
        />
      </div>
    </div>
  )
}
